import { getUserId } from "../../../../lib/effectiveAuthUser";
import * as Sentry from "@sentry/react";
import type { Editor } from "@tiptap/core";
import { db } from "src/Firebase";
import {
  clearPendingStreamChunkBoundary,
  splitStreamChunkLines,
} from "../../Utils";
import { MarkdownStreamer } from "../../markdownStreamer";

interface MutableRef<T> {
  current: T;
}

interface ComparisonEditors {
  editorA?: Editor;
  editorB?: Editor;
}

interface DraftStateMachine {
  events: {
    proseReceived: () => void;
    proseCompleted: () => void;
  };
}

interface SceneDraft {
  lastCommandIndex: number;
  restartFromIndex: number;
  generationFinished: boolean;
}

type ModeMap = Record<string, string>;

export interface ComparisonChunkContext {
  comparisonModeRef: MutableRef<boolean>;
  comparisonEditorsRef: MutableRef<ComparisonEditors | null>;
  comparisonInvocationIdRef: MutableRef<string | null>;
  streamBCommandIndex: MutableRef<number>;
  streamBSceneIndexRef: MutableRef<number | null>;
  comparisonVisibleFired: MutableRef<boolean>;
  localInvocationId: MutableRef<string | null>;
  baseCommandIndex: number;

  setComparisonMode: (value: boolean) => void;
  setComparisonVisible: (value: boolean) => void;
  setScenes: (updater: (draft: Record<string, SceneDraft>) => void) => void;
  setProseWords: (value: number) => void;
  setCurrentProseMode: (value: string) => void;
  setRequesting: (value: boolean) => void;
  currentProseModeRef: MutableRef<string | null>;
  modeMap: ModeMap;
  draftStateMachine: DraftStateMachine;
  documentId: string;
  sceneId: string;
  editor: Editor;
  streamerA?: MarkdownStreamer;
  streamerB?: MarkdownStreamer;
}

export interface StreamChunk {
  comparisonMode?: boolean;
  initialInvocationId?: string;
  text?: string;
  stream?: "A" | "B";
  incrementScene?: number;
  mode?: string;
}

export function updateSingleEditor(
  targetEditor: Editor,
  chunkText: string,
  streamer?: MarkdownStreamer
) {
  if (!chunkText || !targetEditor) return;
  targetEditor.commands.focus("end");

  const lines = splitStreamChunkLines(chunkText, targetEditor);
  if (chunkText.indexOf("\n") === -1 && lines.length === 1) {
    const html = streamer ? streamer.write(lines[0]) : lines[0];
    if (html) targetEditor.commands.insertContent(html);
  } else {
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length > 0) {
        const html = streamer ? streamer.write(lines[i]) : lines[i];
        if (html) targetEditor.commands.insertContent(html);
      }
      if (i < lines.length - 1) {
        if (streamer) {
          const pendingHtml = streamer.flush();
          if (pendingHtml) targetEditor.commands.insertContent(pendingHtml);
        }
        // Plain paragraph boundary — no spacer empty paragraph; visual
        // separation comes from the Paragraph Spacing setting.
        targetEditor.commands.insertContent([{ type: "paragraph", content: [] }]);
      }
    }
  }
}

function updateProseMode(chunk: StreamChunk, ctx: ComparisonChunkContext) {
  const mode = ctx.modeMap[chunk.mode!];
  if (mode && mode !== ctx.currentProseModeRef.current) {
    ctx.setCurrentProseMode(mode);
    ctx.currentProseModeRef.current = mode;
  }
}

export function initializeComparisonMode(ctx: ComparisonChunkContext) {
  ctx.comparisonModeRef.current = true;
  ctx.setComparisonMode(true);
  ctx.streamBCommandIndex.current = ctx.baseCommandIndex;
  ctx.streamerA?.reset();
  ctx.streamerB?.reset();
  const editors = ctx.comparisonEditorsRef.current;
  clearPendingStreamChunkBoundary(editors?.editorA);
  clearPendingStreamChunkBoundary(editors?.editorB);
  setTimeout(() => {
    const mountedEditors = ctx.comparisonEditorsRef.current;
    const editorA = mountedEditors?.editorA;
    const editorB = mountedEditors?.editorB;
    if (editorA && !editorA.isDestroyed) {
      editorA.commands.clearContent();
    }
    if (editorB && !editorB.isDestroyed) {
      editorB.commands.clearContent();
    }
  }, 50);
}

export function persistComparisonInvocationId(chunk: StreamChunk, ctx: ComparisonChunkContext) {
  ctx.localInvocationId.current = chunk.initialInvocationId!;
  const userId = getUserId();
  if (userId && ctx.documentId) {
    db.collection("userData")
      .doc(userId)
      .collection("documentMetas")
      .doc(ctx.documentId)
      .update({ pendingComparisonInvocationId: chunk.initialInvocationId })
      .catch((e: unknown) => Sentry.captureException(e));
  }
}

export function handleComparisonChunk(chunk: StreamChunk, ctx: ComparisonChunkContext) {
  if (chunk.text) {
    let editor;

    if (chunk.stream === "A") {
      editor = ctx.comparisonEditorsRef.current?.editorA;
    } else if (chunk.stream === "B") {
      editor = ctx.comparisonEditorsRef.current?.editorB;
    }

    if (editor) {
      const streamer = chunk.stream === "A" ? ctx.streamerA : ctx.streamerB;
      updateSingleEditor(editor, chunk.text, streamer);
    }

    if (!ctx.comparisonVisibleFired.current) {
      ctx.comparisonVisibleFired.current = true;
      ctx.setComparisonVisible(true);
    }
  }

  if (chunk.incrementScene !== undefined) {
    if (chunk.stream === "A") {
      ctx.setScenes((draft) => {
        draft[ctx.sceneId].lastCommandIndex = (draft[ctx.sceneId].lastCommandIndex || 0) + 1;
        draft[ctx.sceneId].restartFromIndex = draft[ctx.sceneId].lastCommandIndex;
        draft[ctx.sceneId].generationFinished = false;
      });
      ctx.setProseWords(0);
    } else if (chunk.stream === "B") {
      ctx.streamBCommandIndex.current++;
      ctx.streamBSceneIndexRef.current = ctx.streamBCommandIndex.current;
    }
  }

  if (chunk.mode && chunk.stream === "A") {
    updateProseMode(chunk, ctx);
  }

  ctx.draftStateMachine.events.proseReceived();
}

export function handleNormalChunk(chunk: StreamChunk, ctx: ComparisonChunkContext) {
  ctx.draftStateMachine.events.proseReceived();

  if (chunk.incrementScene !== undefined) {
    ctx.setScenes((draft) => {
      draft[ctx.sceneId].lastCommandIndex = (draft[ctx.sceneId].lastCommandIndex || 0) + 1;
      draft[ctx.sceneId].restartFromIndex = draft[ctx.sceneId].lastCommandIndex;
      draft[ctx.sceneId].generationFinished = false;
    });
    ctx.setProseWords(0);
  }

  if (chunk.mode) {
    updateProseMode(chunk, ctx);
  }
}

/**
 * Returns true if the error was an abort during comparison mode (i.e. user
 * paused), in which case comparison state is preserved via handleComparisonEnd.
 * Returns false for all other errors so the caller can run its normal cleanup.
 */
export function handleComparisonAbortError(
  err: { name?: string; message?: string } | null | undefined,
  ctx: ComparisonChunkContext
): boolean {
  const isAbort = err?.name === "AbortError" || err?.message?.includes("abort");
  if (ctx.comparisonModeRef.current && isAbort) {
    handleComparisonEnd(ctx);
    return true;
  }
  return false;
}

export function handleComparisonEnd(ctx: ComparisonChunkContext) {
  // Flush any remaining markdown from both streamers
  const editorA = ctx.comparisonEditorsRef.current?.editorA;
  const editorB = ctx.comparisonEditorsRef.current?.editorB;
  if (ctx.streamerA && editorA) {
    const remaining = ctx.streamerA.flush();
    if (remaining) editorA.commands.insertContent(remaining);
  }
  if (ctx.streamerB && editorB) {
    const remaining = ctx.streamerB.flush();
    if (remaining) editorB.commands.insertContent(remaining);
  }

  ctx.editor.setOptions({ editable: true });
  ctx.setRequesting(false);
  ctx.draftStateMachine.events.proseCompleted();

  const invocationId = ctx.localInvocationId.current;
  if (invocationId) {
    ctx.comparisonInvocationIdRef.current = invocationId;
    const userId = getUserId();
    if (userId && ctx.documentId) {
      db.collection("userData")
        .doc(userId)
        .collection("documentMetas")
        .doc(ctx.documentId)
        .update({
          pendingComparisonInvocationId: invocationId,
          pendingComparisonStreamBSceneIndex: ctx.streamBSceneIndexRef.current,
        })
        .catch((e: unknown) => Sentry.captureException(e));
    }
  }
}
